import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, errorMessage, isCode } from "../api/client.ts";
import type { NetworkItem, NetworkPayment, NetworksData, VaultItem } from "../api/types.ts";
import { CodeInput } from "../components/CodeInput.tsx";
import { DataTable, type Column } from "../components/DataTable.tsx";
import { Dialog } from "../components/Dialog.tsx";
import { useToast } from "../components/Toast.tsx";
import { Alert, AssetChip, CopyButton, EmptyState, Hash, Spinner } from "../components/ui.tsx";
import { fmt, intlLocale, rich, useT } from "../i18n/index.tsx";
import { chainName, formatUnits, parseUnits } from "../lib/format.ts";
import { DAILY_PRESETS, LimitField, PER_TX_PRESETS } from "./VaultActions.tsx";
import { useAsync } from "../lib/hooks.ts";
import { useMe, useSession } from "../lib/session.tsx";

/** Element id of a vault's section on the page (for "set payout limits" links). */
export const vaultAnchor = (asset: string) => `vault-${asset.replace(/[^a-z0-9]+/gi, "-")}`;

const isTron = (chain: string) => chain === "tron";
const ownerOk = (chain: string, v: string) => (isTron(chain) ? /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(v) : /^0x[0-9a-fA-F]{40}$/.test(v));
const settingUp = (s: NetworkItem["status"]) => s === "pending" || s === "deploying";

/** "100.00" → "100", "99.50" → "99.50" (USD amounts shown as USDT). */
const usd = (v: string) => (/\.00$/.test(v) ? v.slice(0, -3) : v);

/** 50 bps → "0.5%" in the console language. */
const pct = (bps: number | null) =>
  bps === null ? "–" : `${(bps / 100).toLocaleString(intlLocale(), { maximumFractionDigits: 2 })}%`;

/**
 * Networks the brand can enable itself: it names the wallet that will own the vault, iCryptoNow deploys the vault.
 * Shown under the vaults, and as the next step when the brand has no vault yet.
 */
export function Networks({ vaults, onActivated }: { vaults: VaultItem[]; onActivated: () => void }) {
  const t = useT();
  const me = useMe();
  const { can } = useSession();
  const inReview = !!me.merchant.in_review;
  const q = useAsync(() => api.get<NetworksData>("/vault/networks"), []);
  const [enable, setEnable] = useState<NetworkItem | null>(null);
  const [payFor, setPayFor] = useState<string | null>(null); // asset whose payment panel is open
  const items = q.data?.items ?? [];
  // Sandbox sign-up accounts get their networks on the shared test vaults; they cannot request more themselves.
  const shared = items.some((n) => n.shared) || vaults.some((v) => v.shared);
  const busy = items.some((n) => settingUp(n.status));
  const awaiting = items.some((n) => n.status === "awaiting_payment");

  // While a network is being set up, check every 5 s; when one turns active, reload the vaults too.
  const setData = q.setData;
  const activeCount = useRef<number | null>(null);
  useEffect(() => {
    const n = items.filter((x) => x.status === "active").length;
    if (activeCount.current !== null && n > activeCount.current) onActivated();
    activeCount.current = n;
  }, [items, onActivated]);
  // Setting up: every 5 s. Waiting for a setup-fee payment: every 10 s.
  useEffect(() => {
    if (!busy && !awaiting) return;
    const timer = setInterval(() => {
      api.get<NetworksData>("/vault/networks").then(setData).catch(() => {});
    }, busy ? 5000 : 10_000);
    return () => clearInterval(timer);
  }, [busy, awaiting, setData]);

  const fee = (raw: string | null, n: NetworkItem) => (raw === null ? "–" : `${formatUnits(raw, n.decimals)} ${n.token}`);
  const st = t.vault.networks.status;

  const cols: Column<NetworkItem>[] = [
    { key: "net", header: t.vault.networks.colNetwork, mobile: "title", cell: (n) => <AssetChip asset={n.asset} chain={n.chain} /> },
    {
      key: "fees", header: t.vault.networks.colFees, cell: (n) => (
        <span className="stack-cell net-fees">
          <span className="num">{fmt(t.vault.networks.feesInOut, { in: pct(q.data?.fee_bps_in ?? null), out: pct(q.data?.fee_bps_out ?? null) })}</span>
          {(n.min_amount_in !== null || n.min_amount_out !== null) && (
            <small className="muted">{fmt(t.vault.networks.minAmounts, { in: fee(n.min_amount_in, n), out: fee(n.min_amount_out, n) })}</small>
          )}
          {(n.min_fee_in !== null || n.min_fee_out !== null) && (
            <small className="muted">
              {fmt(q.data?.fee_formula === "sum" ? t.vault.networks.processingFees : t.vault.networks.minFees, { in: fee(n.min_fee_in, n), out: fee(n.min_fee_out, n) })}
            </small>
          )}
          {n.status !== "active" && n.setup_fee_usd !== undefined && (
            <small className="setup-fee">
              {Number(n.setup_fee_usd) > 0 ? fmt(t.vault.networks.setupFee, { amount: usd(n.setup_fee_usd) }) : t.vault.networks.setupFree}
            </small>
          )}
        </span>
      ),
    },
    {
      key: "status", header: t.vault.networks.colStatus, cell: (n) => (
        <span className="stack-cell">
          <span className={`pill no-dot ${n.status === "active" ? "pill-green" : n.status === "failed" ? "pill-red" : n.status === "available" ? "pill-grey" : "pill-amber"}`}>
            {st[n.status] ?? n.status}
          </span>
          {n.status === "failed" && n.request?.error && <small className="err-text">{fmt(t.vault.networks.failedError, { error: n.request.error })}</small>}
          {n.request && !n.shared && n.status !== "failed" && n.status !== "active" && (
            <small className="muted" title={n.request.owner}>{fmt(t.vault.networks.requestedOwner, { owner: `${n.request.owner.slice(0, 6)}…${n.request.owner.slice(-4)}` })}</small>
          )}
        </span>
      ),
    },
    {
      key: "act", header: t.vault.networks.colAction, align: "right", cell: (n) => {
        if (n.status === "active") {
          const v = vaults.find((x) => x.asset === n.asset);
          return (
            <span className="stack-cell net-active">
              {n.vault_address && <a href={`#${vaultAnchor(n.asset)}`} className="net-vault-link"><Hash value={n.vault_address} copy={false} /></a>}
              {v?.per_tx_limit === "0" && <a href={`#${vaultAnchor(n.asset)}`} className="small">{t.vault.networks.setLimitsNext}</a>}
            </span>
          );
        }
        if (settingUp(n.status)) return <span className="net-progress"><Spinner /> {t.vault.networks.settingUp}</span>;
        if (n.status === "review") return <span className="muted small">{t.vault.networks.waitingReview}</span>;
        if (n.status === "awaiting_payment") {
          return <button type="button" className="btn btn-sm btn-primary" onClick={() => setPayFor(n.asset)}>{t.vault.networks.pay}</button>;
        }
        // available or failed
        if (shared) return <span className="muted small">{t.vault.shared.networkNote}</span>;
        if (!can("vault:edit")) return <span className="muted small">{t.vault.networks.askOwner}</span>;
        return (
          <button type="button" className={`btn btn-sm ${n.status === "failed" ? "btn-ghost" : "btn-primary"}`} onClick={() => setEnable(n)}
            disabled={inReview} title={inReview ? t.shell.inReviewHint : undefined}>
            {n.status === "failed" ? t.vault.networks.retry : t.vault.networks.enable}
          </button>
        );
      },
    },
  ];

  return (
    <section className="section-block networks" aria-labelledby="net-h">
      <div className="section-head">
        <h2 id="net-h">{t.vault.networks.title}</h2>
      </div>
      <p className="muted small net-intro">{fmt(t.vault.networks.intro, { merchant: me.merchant.name })}</p>
      {inReview && <p className="small net-intro net-review">{t.shell.inReviewHint}</p>}
      {q.error ? (
        <Alert tone="error" action={<button className="btn btn-ghost btn-sm" onClick={() => void q.reload()}>{t.common.retry}</button>}>{errorMessage(q.error)}</Alert>
      ) : (
        <DataTable caption={t.vault.networks.title} columns={cols} rows={items} rowKey={(n) => n.asset} loading={q.loading}
          empty={<EmptyState title={t.vault.networks.noNetworks} />} />
      )}
      {enable && (
        <EnableDialog
          network={enable}
          onClose={() => setEnable(null)}
          onSent={(status) => {
            const asset = enable.asset;
            setEnable(null);
            void q.reload();
            if (status === "awaiting_payment") setPayFor(asset);
          }}
        />
      )}
      {payFor && (() => {
        const n = items.find((x) => x.asset === payFor);
        return n ? <PaymentDialog network={n} onClose={() => setPayFor(null)} onCancelled={() => { setPayFor(null); void q.reload(); }} /> : null;
      })()}
    </section>
  );
}

function EnableDialog({ network: n, onClose, onSent }: { network: NetworkItem; onClose: () => void; onSent: (status: string) => void }) {
  const t = useT();
  const me = useMe();
  const toast = useToast();
  const [owner, setOwner] = useState("");
  const whole = (raw: string | undefined, fallback: string) => (raw ? formatUnits(raw, n.decimals, 0, n.decimals).replace(/,/g, "") : fallback);
  const [perTx, setPerTx] = useState(whole(n.default_per_tx, "50000"));
  const [daily, setDaily] = useState(whole(n.default_daily, "1000000"));
  // Float follows the daily limit until the person edits it.
  const [floatText, setFloatText] = useState(whole(n.default_float ?? n.default_daily, "1000000"));
  const [floatEdited, setFloatEdited] = useState(false);
  const floatValue = floatEdited ? floatText : daily;
  const floatRaw = parseUnits(floatValue, n.decimals);
  const [cold, setCold] = useState("");
  const [autoDaily, setAutoDaily] = useState(true);
  const hourUtc = n.default_auto_cold_hour_utc ?? 16;
  // Default: the excess goes back to the owner wallet. Unchecked: a separate cold wallet (or none).
  const [toOwner, setToOwner] = useState(true);
  const ownerAddr = owner.trim();
  const ownerValid = ownerOk(n.chain, ownerAddr);
  const coldAddr = toOwner ? (ownerValid ? ownerAddr : "") : cold.trim();
  const coldErr = !toOwner && coldAddr && !ownerOk(n.chain, coldAddr) ? fmt(t.vault.addressInvalid, { chain: chainName(n.chain) }) : null;
  /** The daily sweep hour in the viewer's local time, e.g. "00:00". */
  const localHour = (() => {
    const d = new Date();
    d.setUTCHours(hourUtc, 0, 0, 0);
    return d.toLocaleTimeString(intlLocale(), { hour: "2-digit", minute: "2-digit" });
  })();
  const perTxRaw = parseUnits(perTx, n.decimals);
  const dailyRaw = parseUnits(daily, n.decimals);
  const limitsErr = !perTxRaw || !dailyRaw
    ? fmt(t.vault.amountInvalid, { decimals: n.decimals })
    : BigInt(dailyRaw) < BigInt(perTxRaw) ? t.vault.dailyBelowPerTx : null;
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const addr = owner.trim();
  const addrErr = addr && !ownerOk(n.chain, addr) ? fmt(t.vault.addressInvalid, { chain: chainName(n.chain) }) : null;
  const vars = { token: n.token, chain: chainName(n.chain), merchant: me.merchant.name };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!addr || addrErr) return setError(fmt(t.vault.addressInvalid, { chain: chainName(n.chain) }));
    if (limitsErr || !perTxRaw || !dailyRaw) return setError(limitsErr);
    if (!floatRaw) return setError(fmt(t.vault.amountInvalid, { decimals: n.decimals }));
    if (coldErr) return setError(coldErr);
    if (code.length !== 6) return setCodeError(t.common.errors.mfa_required);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      const body: Record<string, unknown> = { asset: n.asset, owner_address: addr, per_tx: perTxRaw, daily: dailyRaw, float: floatRaw, code };
      // Always explicit: the owner address, a separate wallet, or "" for none.
      if (coldAddr) Object.assign(body, { cold_wallet: coldAddr, auto_cold: autoDaily ? "daily" : "off", hour_utc: hourUtc });
      else Object.assign(body, { cold_wallet: "", auto_cold: "off" });
      const r = await api.post<{ id: string; status: string }>("/vault/networks", body);
      if (r.status !== "awaiting_payment") toast(t.vault.networks.submitted);
      onSent(r.status);
    } catch (err) {
      setCode("");
      if (isCode(err, "mfa_required")) setCodeError(t.common.errors.mfa_required);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} busy={busy} title={fmt(t.vault.networks.dialogTitle, vars)} description={fmt(t.vault.networks.dialogIntro, vars)}>
      <form className="form-stack" onSubmit={submit} noValidate>
        {n.status === "failed" && n.request?.error && <Alert tone="error">{fmt(t.vault.networks.failedError, { error: n.request.error })}</Alert>}
        <div className="field">
          <label htmlFor="net-owner">{t.vault.networks.ownerLabel}</label>
          <input id="net-owner" className="mono-input" value={owner} onChange={(e) => setOwner(e.target.value)} onBlur={() => setTouched(true)}
            placeholder={isTron(n.chain) ? "T…" : "0x…"} spellCheck={false} autoComplete="off" autoCapitalize="off"
            aria-invalid={touched && !!addrErr} aria-describedby="net-owner-warn" />
          {touched && addrErr && <p className="field-error">{addrErr}</p>}
        </div>
        <div id="net-owner-warn" className="owner-warning" role="note">{rich(t.vault.networks.ownerWarning)}</div>
        <div className="field-row">
          <LimitField id="net-pertx" label={t.vault.networks.perTxLabel} value={perTx} onChange={setPerTx} token={n.token} presets={PER_TX_PRESETS} />
          <LimitField id="net-daily" label={t.vault.networks.dailyLabel} value={daily} onChange={setDaily} token={n.token} presets={DAILY_PRESETS} />
        </div>
        <p className={limitsErr ? "field-error" : "field-hint"}>{limitsErr ?? t.vault.networks.limitsHelp}</p>

        <fieldset className="field cold-section">
          <legend>{t.vault.networks.coldSection}</legend>
          <label className="check">
            <input type="checkbox" checked={toOwner} onChange={(e) => setToOwner(e.target.checked)} />
            <span><strong>{t.vault.networks.coldToOwner}</strong></span>
          </label>
          {toOwner ? (
            <div className="cold-dest">
              <span className="field-label">{t.vault.networks.coldGoesTo}</span>
              {ownerValid ? <Hash value={ownerAddr} /> : <span className="muted small">{t.vault.networks.coldOwnerFirst}</span>}
            </div>
          ) : (
            <div className="field">
              <label htmlFor="net-cold">{t.vault.networks.coldOtherLabel} <span className="optional">{t.common.optional}</span></label>
              <input id="net-cold" className="mono-input" value={cold} onChange={(e) => setCold(e.target.value)}
                placeholder={isTron(n.chain) ? "T…" : "0x…"} spellCheck={false} autoComplete="off" autoCapitalize="off"
                aria-invalid={!!coldErr} aria-describedby="net-cold-hint" />
              <p id="net-cold-hint" className={coldErr ? "field-error" : "field-hint"}>
                {coldErr ?? <>{t.vault.networks.coldHint} {!coldAddr && t.vault.networks.coldEmptyHint}</>}
              </p>
            </div>
          )}
          <p className="field-hint cold-guide">{t.vault.networks.coldGuide}</p>
          <div className="field net-float">
            <label htmlFor="net-float">{t.vault.networks.floatLabel}</label>
            <div className="input-affix">
              <input id="net-float" inputMode="decimal" autoComplete="off" value={floatValue}
                onChange={(e) => { setFloatEdited(true); setFloatText(e.target.value); }} aria-invalid={!floatRaw} />
              <span className="affix">{n.token}</span>
            </div>
            <p className={floatRaw ? "field-hint" : "field-error"}>{floatRaw ? t.vault.networks.floatHint : fmt(t.vault.amountInvalid, { decimals: n.decimals })}</p>
          </div>
          <label className={`check ${coldAddr && !coldErr ? "" : "is-disabled"}`}>
            <input type="checkbox" checked={autoDaily && !!coldAddr && !coldErr} disabled={!coldAddr || !!coldErr} onChange={(e) => setAutoDaily(e.target.checked)} />
            <span>{fmt(t.vault.networks.autoDaily, { local: localHour, hour: String(hourUtc).padStart(2, "0") })}</span>
          </label>
        </fieldset>
        <CodeInput value={code} onChange={(c) => { setCode(c); setCodeError(null); }} error={codeError} disabled={busy} />
        {error && <Alert tone="error">{error}</Alert>}
        {Number(n.setup_fee_usd ?? "0") > 0 && (
          <p className="fee-line">{fmt(t.vault.networks.feeLine, { amount: usd(n.setup_fee_usd ?? "0"), chains: "TRON / BNB Chain" })}</p>
        )}
        <div className="dlg-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>{t.common.cancel}</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy && <Spinner />} {t.vault.networks.submit}</button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Setup-fee payment: the amount still due, one tab per payment network with the address (QR + copy) and accepted tokens,
 * and a status line that follows the request (checked every 10 s by the table): waiting → payment received → ready.
 */
function PaymentDialog({ network: n, onClose, onCancelled }: { network: NetworkItem; onClose: () => void; onCancelled: () => void }) {
  const t = useT();
  const tn = t.vault.networks;
  const { can } = useSession();
  const toast = useToast();
  // Keep the last payment details: once paid, the request moves on (pending/deploying) and no longer carries them.
  const [payment, setPayment] = useState<NetworkPayment | null>(n.request?.payment ?? null);
  useEffect(() => {
    if (n.request?.payment) setPayment(n.request.payment);
  }, [n.request?.payment]);
  const [tab, setTab] = useState(0);
  const [qr, setQr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addr = payment?.addresses[tab] ?? payment?.addresses[0];
  const fee = Number(payment?.fee_usd ?? 0);
  // Once the request moves on (pending, deploying, active) the fee has been paid in full.
  const paid = n.status === "awaiting_payment" ? Number(payment?.paid_usd ?? 0) : fee;
  const due = Math.max(0, fee - paid).toFixed(2);
  const waiting = n.status === "awaiting_payment";
  const creating = n.status === "pending" || n.status === "deploying" || n.status === "review";

  useEffect(() => {
    if (!addr) return;
    let alive = true;
    import("qrcode")
      .then(({ default: QRCode }) => QRCode.toDataURL(addr.address, { margin: 1, width: 320, color: { dark: "#0b1e46", light: "#ffffff" } }))
      .then((u) => alive && setQr(u))
      .catch(() => alive && setQr(null));
    return () => {
      alive = false;
    };
  }, [addr]);

  const cancel = async () => {
    if (!n.request) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/vault/networks/${encodeURIComponent(n.request.id)}/cancel`);
      toast(tn.cancelled);
      onCancelled();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} busy={busy} title={tn.payTitle} description={fmt(tn.payIntro, { token: n.token, chain: chainName(n.chain) })}>
      <div className="form-stack">
        {payment && (
          <div className="pay-amount">
            <span className="muted">{tn.amountDue}</span>
            <strong>{usd(due)} USDT</strong>
            <span className="muted small">{fmt(tn.paidSoFar, { paid: usd(paid.toFixed(2)), fee: usd(fee.toFixed(2)) })}</span>
          </div>
        )}

        {waiting && payment && payment.addresses.length > 0 && addr && (
          <>
            {payment.addresses.length > 1 && (
              <div className="seg" role="tablist" aria-label={tn.payNetwork}>
                {payment.addresses.map((a, i) => (
                  <button key={a.chain} type="button" role="tab" aria-selected={i === tab} aria-pressed={i === tab} onClick={() => setTab(i)}>
                    {chainName(a.chain)}
                  </button>
                ))}
              </div>
            )}
            <div className="pay-address" role="tabpanel">
              <div className="qr">{qr ? <img src={qr} alt={tn.qrAlt} width={160} height={160} /> : <Spinner />}</div>
              <div className="pay-address-text">
                <span className="field-label">{chainName(addr.chain)} · {fmt(tn.acceptedTokens, { tokens: addr.tokens.join(", ") })}</span>
                <div className="owner-address"><code>{addr.address}</code><CopyButton value={addr.address} label={t.common.copy} /></div>
                <p className="pay-warning">{fmt(tn.sendOnly, { tokens: addr.tokens.join("/"), chain: chainName(addr.chain) })}</p>
              </div>
            </div>
          </>
        )}

        <p className="waiting-line" aria-live="polite">
          {waiting && <><Spinner /> {tn.waitingPayment}</>}
          {creating && <><Spinner /> {tn.paymentReceived}</>}
          {n.status === "active" && <span className="ok-text">✓ {tn.vaultReady}</span>}
          {n.status === "failed" && n.request?.error && <span className="err-text">{fmt(tn.failedError, { error: n.request.error })}</span>}
        </p>
        {error && <Alert tone="error">{error}</Alert>}
        <div className="dlg-actions">
          {waiting && paid === 0 && can("vault:edit") && (
            <button type="button" className="btn btn-ghost btn-danger-ghost" onClick={() => void cancel()} disabled={busy}>{busy && <Spinner />} {tn.cancelRequest}</button>
          )}
          <button type="button" className="btn btn-primary" onClick={onClose}>{n.status === "active" ? t.common.done : t.common.close}</button>
        </div>
      </div>
      <p className="guide-help muted small">
        {rich(t.vault.guide.help, { a: (c) => <a href="https://t.me/mrho68" target="_blank" rel="noreferrer noopener">{c}</a> })}
      </p>
    </Dialog>
  );
}
